'use client';
// components/sections/ClientStories.tsx
import Image from 'next/image';
import { useEffect, useRef, useState, useMemo } from 'react';
import { FaQuoteLeft, FaStar } from 'react-icons/fa';
import Button from '../ui/Button';
import SectionTitle from '../ui/SectionTitle';

interface Testimonial {
  description: string;
  author: string;
  avatar: string;
  ratingValue: number;
  country?: string; // optional, used only for filtering
}

interface ClientStoriesProps {
  title?: string;
  description?: string;
  backgroundClassName?: string;
  showViewMore?: boolean;
  countryFilter?: string; // e.g. "USA", "Canada" – optional, filters testimonials
}

// Testimonials data with optional country field
const allTestimonials: Testimonial[] = [
  {
    description:
      'They did a great job with my website ranking! They were very knowledgeable and was able to sort out the mess that was my analytics tracking, helping Google to successfully index and track my pages again. It was a HUGE help and I couldn‘t have done it without them! Also, I really appreciated how responsive they were to my messages. Responses were quick, and tasks were accomplished well. I could tell they knew what they were doing! I would definitely hire MMB Digital Lab‘s again for future work!',
    author: 'Angela Hooper',
    avatar: '/testimonial-data/angela-hooper.png',
    ratingValue: 5.0,
  },
  {
    description:
      'Make My Brand Labs is currently delivering excellent work on a reasonable budget. They have all my gratitude for their hard work up to this point. I hope to continue this working relationship forward and am sure that their services will take my business to the next level. If you want to hire professionals who know what they are doing, definitely get in touch with them.',
    author: 'Tucker Lee',
    avatar: '/testimonial-data/Tucker-lee-avtar.webp',
    ratingValue: 5.0,
  },
  {
    description:
      'I reached out to team MMB Digital Lab‘s to develop an investor pitch deck, and it was indeed a brilliant journey. From the beginning, the team focused on understanding the requirements properly and therefore presented a deck worth presenting in front of the investors. I highly regard their ability to perform tasks sincerely, and regular communication with the project lead made the entire process easier. I look forward to my association with MMB Digital Lab‘s for future projects as well.',
    author: 'Bernardo Rubie',
    avatar: '/testimonial-data/Bernardo-avtar.png',
    ratingValue: 5.0,
  },
  {
    description:
      'We‘re impressed with their willingness to help us with any marketing tasks. Through Make My Brand Labs‘ efforts, the client has seen an increase in engagement on their social media posts. The team is very easy to work with and always willing to help in every way they can. Overall, customers can expect a supportive and reliable partner.',
    author: 'Stefanie Amini',
    avatar: '/testimonial-data/Stefanie-Amini-avtar.png',
    ratingValue: 5.0,
  },
  {
    description:
      'Working with MMB Digital Lab‘s was a wonderful experience. They have excellent strategies to cope with our competitors through their competitor analysis. Their team worked through our demands and created captivating content for our website, resulting in a 40% increase in clicks within a month. MMB Digital Labs‘s consultants also helped us with off-page SEO and on-page SEO to make our website appealing. I highly recommend their services if you want to improve your online presence.',
    author: 'Noah Warren',
    avatar: '/testimonial-data/Noah-Warren-avtar.png',
    ratingValue: 5.0,
  },
  {
    description:
      'PPC Expert Adwords & Facebook. Outstanding job, very professional and produced great results. I will definitely hire them again and would highly recommend them for future work.',
    author: 'Conston Taylor',
    avatar: '/testimonial-data/Conston-Taylor-avtar.webp',
    ratingValue: 5.0,
    country: 'USA',
  },
  {
    description:
      'Make My Brand Labs paid marketing techniques helped us reach a large pool of potential clients and improved our brand awareness. Make My Brand Labs has the finest strategists and consultants for Google AdWords to work with to increase your brands digital presence. They made sure all our requirements were covered and gave us tremendous results in just one and a half months.',
    author: 'Adam Cohen ',
    avatar: '/testimonial-data/Adam_Cohen.avif',
    ratingValue: 5.0,
    country: 'USA',
  },
  {
    description:
      'Make My Brand Labs has been an excellent partner in my journey of marketing success, offering all-encompassing services, including SEO, keyword research, PPC and SMM. I enjoyed working with them a lot.',
    author: 'Daniel Linnan',
    avatar: '/testimonial-data/Daniel.avif',
    ratingValue: 5.0,
    country: 'USA',
  },
  {
    description:
      'Make My Brand Labs working on our Google and Bing Ads campaigns has been a game-changer. Their strategic approach and expert execution have significantly boosted traffic and conversions across multiple websites. Hire them if you want results.',
    author: 'Jordan Rhodes',
    avatar: '/testimonial-data/Rhodes.avif',
    ratingValue: 5.0,
    country: 'USA',
  },
];

export default function ClientStories({
  title = 'You are in Good Company',
  description = 'Hear from our clients as they share their journeys of transformation.',
  backgroundClassName = 'bg-white',
  countryFilter,
}: ClientStoriesProps) {
  // Filter testimonials based on the optional countryFilter
  const testimonials = useMemo(() => {
    if (!countryFilter) return allTestimonials;
    const lowerFilter = countryFilter.toLowerCase();
    return allTestimonials.filter(
      (t) => t.country && t.country.toLowerCase() === lowerFilter
    );
  }, [countryFilter]);

  const TOTAL_SLOTS = 4;
  const isFiltered = !!countryFilter;

  // If no testimonials match the filter, show a fallback message
  const hasTestimonials = testimonials.length > 0;

  // For filtered view: we want exactly 4 static testimonials (or fewer if not enough)
  // We'll take the first TOTAL_SLOTS indices.
  const getCyclicIndex = (index: number): number => index % testimonials.length;

  // Determine initial visible indices
  let initialVisible: number[];
  if (isFiltered) {
    // When filtering, show the first TOTAL_SLOTS testimonials (static, no rotation)
    initialVisible = Array.from(
      { length: Math.min(TOTAL_SLOTS, testimonials.length) },
      (_, i) => i
    );
  } else {
    // Original behaviour: fill up to TOTAL_SLOTS, cyclically if needed
    initialVisible = Array.from(
      { length: Math.min(TOTAL_SLOTS, testimonials.length) },
      (_, i) => i
    );
  }

  // If filtered list has fewer than TOTAL_SLOTS, we still want to fill 4 slots cyclically
  // (but for USA we have exactly 4, so this is a safe fallback)
  const finalInitialVisible =
    testimonials.length > 0 && testimonials.length < TOTAL_SLOTS && !isFiltered
      ? Array.from({ length: TOTAL_SLOTS }, (_, i) => getCyclicIndex(i))
      : initialVisible;

  const [visibleIndices, setVisibleIndices] = useState<number[]>(finalInitialVisible);
  const [fadeFlags, setFadeFlags] = useState<boolean[]>(Array(TOTAL_SLOTS).fill(false));
  const [slotVersions, setSlotVersions] = useState<number[]>(Array(TOTAL_SLOTS).fill(0));

  const pickDiagonalPair = (): Set<number> => {
    const pairs: [number, number][] = [
      [0, 3],
      [1, 2],
    ];
    const chosen = pairs[Math.floor(Math.random() * pairs.length)];
    return new Set<number>(chosen);
  };

  const [alwaysActiveSlots, setAlwaysActiveSlots] = useState<Set<number>>(new Set());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setAlwaysActiveSlots(pickDiagonalPair());
  }, []);

  const timeoutRef = useRef<number | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const rightPanelRef = useRef<HTMLDivElement | null>(null);
  const topRowRef = useRef<HTMLDivElement | null>(null);
  const bottomRowRef = useRef<HTMLDivElement | null>(null);
  const parallaxRef = useRef<{
    topCurrent: number;
    bottomCurrent: number;
    topTarget: number;
    bottomTarget: number;
    lastTs: number | null;
  }>({
    topCurrent: 0,
    bottomCurrent: 0,
    topTarget: 0,
    bottomTarget: 0,
    lastTs: null,
  });

  // Rotation effect – completely disabled when a country filter is active
  useEffect(() => {
    if (!hasTestimonials || isFiltered) return; // No rotation when filtering

    const scheduleNext = () => {
      const delayMs = 7000 + Math.floor(Math.random() * 1000);
      timeoutRef.current = window.setTimeout(() => {
        setVisibleIndices((prev) => {
          const slotToReplace = Math.floor(Math.random() * TOTAL_SLOTS);

          const currentlyVisibleSet = new Set(prev);
          const allIndices = Array.from({ length: testimonials.length }, (_, i) => i);
          const candidates = allIndices.filter((idx) => !currentlyVisibleSet.has(idx));

          let nextIndex: number;
          if (candidates.length > 0) {
            nextIndex = candidates[Math.floor(Math.random() * candidates.length)];
          } else {
            const otherIndices = allIndices.filter((idx) => idx !== prev[slotToReplace]);
            nextIndex =
              otherIndices.length > 0
                ? otherIndices[Math.floor(Math.random() * otherIndices.length)]
                : prev[slotToReplace];
          }

          setFadeFlags((old) => {
            const copy = [...old];
            copy[slotToReplace] = true;
            return copy;
          });

          window.setTimeout(() => {
            setSlotVersions((vers) => {
              const vcopy = [...vers];
              vcopy[slotToReplace] = vcopy[slotToReplace] + 1;
              return vcopy;
            });
            setFadeFlags((old) => {
              const copy = [...old];
              copy[slotToReplace] = false;
              return copy;
            });
          }, 300);

          const next = [...prev];
          next[slotToReplace] = nextIndex;
          return next;
        });

        setAlwaysActiveSlots(pickDiagonalPair());
        scheduleNext();
      }, delayMs);
    };

    scheduleNext();
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [testimonials, hasTestimonials, isFiltered]);

  // (Existing parallax + sticky effect – unchanged)
  useEffect(() => {
    const panel = rightPanelRef.current;
    const sectionEl = sectionRef.current;
    const topEl = topRowRef.current;
    const bottomEl = bottomRowRef.current;
    if (!panel || !sectionEl || !topEl || !bottomEl) return;

    const mql = window.matchMedia('(min-width: 1280px)');
    let removeHandlers: (() => void) | null = null;

    const setupAnimation = () => {
      let frameId: number | null = null;
      let animId: number | null = null;

      const clamp = (value: number, min: number, max: number) =>
        Math.max(min, Math.min(max, value));

      const animateTowardsTargets = (ts: number) => {
        const state = parallaxRef.current;
        if (state.lastTs === null) state.lastTs = ts;
        const dt = Math.max(0.001, Math.min(0.033, (ts - state.lastTs) / 1000));
        state.lastTs = ts;

        const lambda = 12;
        const alpha = 1 - Math.exp(-lambda * dt);

        const nextTop = state.topCurrent + (state.topTarget - state.topCurrent) * alpha;
        const nextBottom = state.bottomCurrent + (state.bottomTarget - state.bottomCurrent) * alpha;

        state.topCurrent = nextTop;
        state.bottomCurrent = nextBottom;

        topEl.style.transform = `translate3d(${nextTop}px,0,0)`;
        bottomEl.style.transform = `translate3d(${nextBottom}px,0,0)`;

        const eps = 0.05;
        const doneTop = Math.abs(state.topTarget - nextTop) < eps;
        const doneBottom = Math.abs(state.bottomTarget - nextBottom) < eps;
        if (doneTop && doneBottom) {
          animId = null;
          return;
        }
        animId = window.requestAnimationFrame(animateTowardsTargets);
      };

      const update = () => {
        const rect = sectionEl.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 1;
        const viewportCenter = viewportH / 2;
        const sectionCenter = rect.top + rect.height / 2;
        const distanceFromCenter = sectionCenter - viewportCenter;

        const factor = 0.06;
        const maxShift = 72;
        parallaxRef.current.topTarget = clamp(-distanceFromCenter * factor, -maxShift, maxShift);
        parallaxRef.current.bottomTarget = clamp(distanceFromCenter * factor, -maxShift, maxShift);

        if (animId === null) {
          animId = window.requestAnimationFrame(animateTowardsTargets);
        }

        const panelHeight = panel.offsetHeight || 0;
        const canStick = rect.top <= viewportCenter && rect.bottom - panelHeight >= viewportCenter;
        panel.style.transform = canStick ? 'translateY(-50%)' : 'translateY(0)';

        frameId = null;
      };

      const onScrollOrResize = () => {
        if (frameId !== null) return;
        frameId = window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize);
      onScrollOrResize();

      return () => {
        window.removeEventListener('scroll', onScrollOrResize);
        window.removeEventListener('resize', onScrollOrResize);
        if (frameId !== null) cancelAnimationFrame(frameId);
        if (animId !== null) cancelAnimationFrame(animId);
      };
    };

    const enableIfNeeded = () => {
      if (removeHandlers) {
        removeHandlers();
        removeHandlers = null;
      }
      if (mql.matches) {
        removeHandlers = setupAnimation();
      } else {
        parallaxRef.current.topCurrent = 0;
        parallaxRef.current.bottomCurrent = 0;
        parallaxRef.current.topTarget = 0;
        parallaxRef.current.bottomTarget = 0;
        parallaxRef.current.lastTs = null;
        topEl.style.transform = '';
        bottomEl.style.transform = '';
        panel.style.transform = '';
      }
    };

    enableIfNeeded();
    const onChange = () => enableIfNeeded();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
    }

    return () => {
      if (removeHandlers) removeHandlers();
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', onChange);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(onChange);
      }
    };
  }, []);

  // If no testimonials match the filter, display a friendly message
  if (!hasTestimonials) {
    return (
      <section className={`pb-8 xl:pb-0 overflow-hidden ${backgroundClassName}`}>
        <div className="custom-container mx-auto text-center py-20">
          <p className="text-gray-500">
            No testimonials found for {countryFilter ? `“${countryFilter}”` : 'the selected country'}.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={`pb-8 xl:pb-0 overflow-hidden ${backgroundClassName}`}
    >
      <div className="custom-container overflow-hidden mx-auto grid xl:grid-cols-[1fr_3fr] xl:gap-40 gap-10">
        {/* Left Panel */}
        <div className="flex flex-col justify-center pt-10">
          <SectionTitle title={title} description={description} align="left" />
          <div className="max-w-3xl mt-8 hidden xl:block">
            <Button
              responsiveSize={{ mobile: 'md', desktop: 'lg' }}
              variant="primary"
              href="/testimonial"
            >
              View More
            </Button>
          </div>
        </div>

        {/* Right Panel: Testimonials */}
        <div
          ref={rightPanelRef}
          className="grid grid-rows-2 gap-y-0 ml-auto xl:sticky xl:top-1/2 transition-transform duration-800"
          style={{
            willChange: 'transform',
            transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          {/* Top Row */}
          <div
            ref={topRowRef}
            className="grid lg:grid-cols-2 items-stretch"
            style={{ willChange: 'transform' }}
          >
            <TestimonialCard
              key={`slot-0-v${slotVersions[0]}`}
              mounted={mounted}
              fadeOut={fadeFlags[0]}
              {...testimonials[getCyclicIndex(visibleIndices[0])]}
              alwaysActive={alwaysActiveSlots.has(0)}
            />
            <TestimonialCard
              key={`slot-1-v${slotVersions[1]}`}
              mounted={mounted}
              fadeOut={fadeFlags[1]}
              {...testimonials[getCyclicIndex(visibleIndices[1])]}
              alwaysActive={alwaysActiveSlots.has(1)}
            />
          </div>

          {/* Bottom Row */}
          <div
            ref={bottomRowRef}
            className="grid lg:grid-cols-2 items-stretch relative xl:-left-[130px]"
            style={{ willChange: 'transform' }}
          >
            <TestimonialCard
              key={`slot-2-v${slotVersions[2]}`}
              mounted={mounted}
              fadeOut={fadeFlags[2]}
              {...testimonials[getCyclicIndex(visibleIndices[2])]}
              alwaysActive={alwaysActiveSlots.has(2)}
            />
            <TestimonialCard
              key={`slot-3-v${slotVersions[3]}`}
              mounted={mounted}
              fadeOut={fadeFlags[3]}
              {...testimonials[getCyclicIndex(visibleIndices[3])]}
              alwaysActive={alwaysActiveSlots.has(3)}
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto xl:hidden">
          <Button
            responsiveSize={{ mobile: 'md', desktop: 'lg' }}
            variant="primary"
            href="/testimonial"
          >
            View More
          </Button>
        </div>
      </div>
    </section>
  );
}

interface TestimonialCardProps {
  quote?: string;
  description: string;
  author: string;
  rating?: number;
  avatar: string;
  alwaysActive: boolean;
  fadeOut: boolean;
  mounted: boolean;
}

function TestimonialCard({
  description,
  author,
  rating = 5,
  avatar,
  alwaysActive = false,
  fadeOut = false,
  mounted = false,
}: TestimonialCardProps) {
  const truncatedDescription =
    description.length > 100
      ? description.substring(0, 200).trim() + '...'
      : description;

  return (
    <div
      className={`transform border xl:-skew-x-20 ${
        mounted && alwaysActive ? 'card-hover-bg' : 'hover:card-hover-bg'
      } to-white xl:border-l xl:border-b border-[#C5C5C5]/50 xl:px-16 xl:py-8 p-4 py-6 relative min-h-auto 2xl:min-h-[300px]`}
    >
      <div className="flex flex-col items-start sm:flex-row sm:gap-4 items-center">
        <div className="flex items-center gap-4">
          <div
            className={`xl:absolute flex-shrink-0 xl:skew-x-20 top-6 mb-4 xl:mb-0 right-6 w-[60px] h-[60px] 2xl:w-[80px] 2xl:h-[80px] rounded-md lg:rounded-full overflow-hidden border-2 border-white shadow transition-all duration-600 ease-in-out ${
              fadeOut
                ? 'opacity-0 translate-y-2 blur-[1px] scale-[0.995]'
                : 'opacity-100 translate-y-0 blur-0 scale-100'
            }`}
            style={{ willChange: 'opacity, transform, filter' }}
          >
            <Image
              src={avatar}
              alt={author}
              fill
              className="object-cover"
              sizes="32px sm:40px"
            />
          </div>

          <div className="sm:hidden">
            <div className="flex items-center justify-between text-sm text-gray-800">
              <div className="flex items-center gap-2">
                <span>{rating} / 5</span>
                <div className="flex gap-1 text-orange-400">
                  {[...Array(rating)].map((_, idx) => (
                    <FaStar key={idx} />
                  ))}
                </div>
              </div>
            </div>
            <p className="font-bold text-sm mt-1">{author}</p>
          </div>
        </div>
      </div>

      <div
        className={`transform xl:skew-x-20 transition-all duration-600 ease-in-out ${
          fadeOut
            ? 'opacity-0 translate-y-2 blur-[1px] scale-[0.995]'
            : 'opacity-100 translate-y-0 blur-0 scale-100'
        }`}
        style={{ willChange: 'opacity, transform, filter' }}
      >
        <FaQuoteLeft className="hidden lg:block text-gray-300 text-3xl mb-4" />
        <p className="text-[#1b1b1b] hidden lg:block text-sm sm:text-base mb-4 sm:mr-12 line-clamp-3">
          {truncatedDescription}
        </p>
        <div className="hidden sm:block">
          <div className="flex items-center justify-between text-sm text-gray-800">
            <div className="flex items-center gap-2">
              <span>{rating} / 5</span>
              <div className="flex gap-1 text-orange-400">
                {[...Array(rating)].map((_, idx) => (
                  <FaStar key={idx} />
                ))}
              </div>
            </div>
          </div>
          <p className="font-bold text-sm mt-1">{author}</p>
        </div>
      </div>
    </div>
  );
}